import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { DinoLogo } from '@/components/DinoLogo';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background w-full max-w-full overflow-x-hidden">
      <header className="sticky top-[var(--header-height)] z-40 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <DinoLogo className="h-8" />
            <div className="w-20" /> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-4xl font-heading font-bold mb-8">Privacy Policy</h1>
          
          <div className="bg-muted/50 border border-border rounded-lg p-6 mb-8">
            <p className="text-lg font-medium mb-2">Detailed Privacy Policy Coming Soon</p>
            <p className="text-muted-foreground">
              We're currently preparing our comprehensive Privacy Policy. This page will be updated with complete privacy details soon.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Our Commitment</h2>
            <p className="mb-4">
              Dino respects your privacy and is committed to protecting your personal data. We believe in transparency and giving you control over your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">What We Collect</h2>
            <p className="mb-4">
              We collect only the information necessary to provide our service:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Account information (email, username, profile details)</li>
              <li>Reading activity and preferences</li>
              <li>Content you post (reviews, ratings, comments)</li>
              <li>Usage data to improve our service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">How We Use Your Data</h2>
            <p className="mb-4">
              Your data is used to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Provide and improve Dino's features</li>
              <li>Personalize your reading experience</li>
              <li>Enable social features and connections</li>
              <li>Communicate important updates</li>
              <li>Maintain platform security</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Data Security</h2>
            <p className="mb-4">
              We implement industry-standard security measures to protect your personal information. Your data is encrypted and stored securely.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Your Rights</h2>
            <p className="mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Access your personal data</li>
              <li>Request corrections to your data</li>
              <li>Delete your account and data</li>
              <li>Control your privacy settings</li>
              <li>Opt out of certain data collection</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Age & Privacy</h2>
            <p className="mb-4">
              Users under 18 have additional privacy protections. Parents and guardians can review our{' '}
              <a href="/community-guidelines" className="text-primary hover:underline">
                Community Guidelines
              </a>{' '}
              for age-specific policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Questions About Privacy?</h2>
            <p>
              If you have questions about how we handle your data, please contact our privacy team or review our{' '}
              <a href="/community-guidelines" className="text-primary hover:underline">
                Community Guidelines
              </a>.
            </p>
          </section>

          <div className="mt-12 pt-8 border-t border-border text-center">
            <Button onClick={() => navigate(-1)} variant="outline">
              Go Back
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
